
import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useAppConfig } from '../context/AppContext';

interface ContactSurveyProps {
  onOpenModal?: (type: 'waitlist' | 'community') => void;
}

const ContactSurvey: React.FC<ContactSurveyProps> = () => {
  const { hasJoinedWaitlist, markJoinedWaitlist } = useAppConfig();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrorMessage('Please enter your name.');
      setStatus('error');
      return;
    }
    if (!role || role === 'Select Your Role') {
      setErrorMessage('Please select your role.');
      setStatus('error');
      return;
    }
    if (!email.trim()) {
      setErrorMessage('Please enter your email.');
      setStatus('error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          role,
          type: 'waitlist'
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to join waitlist.');
      }

      markJoinedWaitlist();
    } catch (err: any) {
      console.error('Submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-4xl mx-auto text-center px-4">
        <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
          {hasJoinedWaitlist ? "Thank You!" : "Get Early Access"}
        </h2>
        <p className="text-slate-500 text-lg mb-12">
          {hasJoinedWaitlist 
            ? "You've successfully joined our priority waitlist community." 
            : "Be the first to know when Pharmelo launches. Join our community of early adopters."}
        </p>
        
        {hasJoinedWaitlist ? (
          <div className="bg-white p-8 md:p-12 rounded-[3rem] shadow-xl border border-slate-200 text-center max-w-xl mx-auto">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600 shadow-sm">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">You're on the list!</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              We've saved your preference. You'll receive priority access as Pharmelo rolls out near you — you're among the first to help shape it.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-[3rem] shadow-xl border border-slate-200 text-left max-w-2xl mx-auto">
            {status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-600 text-sm font-medium">
                <AlertCircle size={16} />
                {errorMessage}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <input 
                type="text" 
                placeholder="Your Name" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all placeholder:text-slate-400" 
                required 
              />
              <select 
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all text-slate-700"
                required
              >
                <option value="">Select Your Role</option>
                <option value="Consumer">Consumer (Customer)</option>
                <option value="Pharmacist">Pharmacist</option>
                <option value="Doctor">Doctor</option>
                <option value="Caregiver">Caregiver</option>
              </select>
            </div>
            <input 
              type="email" 
              placeholder="Email Address" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl mb-6 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all placeholder:text-slate-400" 
              required 
            />
            
            <label className="flex items-center gap-3 mb-8 cursor-pointer justify-center">
              <input type="checkbox" className="w-5 h-5 rounded border-slate-300 text-emerald-600" defaultChecked required />
              <span className="text-sm font-medium text-slate-700">Notify me when Pharmelo launches</span>
            </label>

            <button 
              type="submit"
              disabled={status === 'submitting'}
              className="w-full sm:w-auto px-12 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold text-lg transition-all shadow-xl shadow-emerald-600/20 flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5" /> Joining...
                </>
              ) : (
                <>
                  Join the Future <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default ContactSurvey;
